import { paths } from '@luxuryescapes/contract-svc-merchandise'
import { arrayToObject } from 'lib/array/arrayUtils'

type Response = paths['/api/merchandise/categories']['get']['responses']['200']['content']['application/json']
type ServerCategory = Response['result']['categories'][0]

/**
 * Orders the ancestors from closest -> furthest away
 * This is how customer portal wishes to use the data
 * @param ancestors All ancestors
 * @returns An array of ordered ancestors
 */
function getOrderedAncestorIds(ancestors: ServerCategory['ancestors']): Array<App.MerchandiseCategoryAncestor> {
  if (!ancestors.length) {
    return []
  }

  const ancestorsByParentId = arrayToObject(ancestors, ancestor => ancestor.parentId ?? 'top')
  let currentAncestor = ancestorsByParentId.top
  const orderedAncestorIds: Array<App.MerchandiseCategoryAncestor> = [{
    id: currentAncestor.id,
    slug: currentAncestor.slug,
  }]
  while (ancestorsByParentId[currentAncestor.id]) {
    currentAncestor = ancestorsByParentId[currentAncestor.id]
    orderedAncestorIds.unshift({
      id: currentAncestor.id,
      slug: currentAncestor.slug,
    })
  }

  return orderedAncestorIds
}

function mapMerchandiseCategory(serverCategory: ServerCategory): App.MerchandiseCategory {
  // Map child categories if they're available in the response
  const childCategories = serverCategory.subCategories.map((subCategory): App.MerchandiseCategory => ({
    id: subCategory.id,
    parentId: subCategory.parentId ?? undefined,
    name: subCategory.displayName,
    level: subCategory.ancestors.length + 1,
    slug: subCategory.slug,
    ancestors: getOrderedAncestorIds(subCategory.ancestors),
    childCategoryIds: [], // Nested subcategories not included in this response
    image: subCategory.displayImageId ? {
      id: subCategory.displayImageId,
      title: subCategory.displayName,
    } : undefined,
    advertCount: subCategory.advertCount ?? undefined,
  }))

  return {
    id: serverCategory.id,
    parentId: serverCategory.parentId ?? undefined,
    name: serverCategory.displayName,
    level: serverCategory.ancestors.length + 1,
    slug: serverCategory.slug,
    ancestors: getOrderedAncestorIds(serverCategory.ancestors),
    childCategoryIds: serverCategory.subCategories.map(subCategory => subCategory.id),
    childCategories: childCategories.length > 0 ? childCategories : undefined,
    image: serverCategory.displayImageId ? {
      id: serverCategory.displayImageId,
      title: serverCategory.displayName,
    } : undefined,
  }
}

export default mapMerchandiseCategory
